import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { ClockIcon, MenuIcon, TagIcon, VoidIcon } from './DashboardUI';

/**
 * The band a manager on shift actually acts on.
 *
 * Only things that are true right now appear here, and each one is a fact with
 * a number attached rather than a warning light. When nothing has gone wrong
 * the whole strip is absent — an empty row of green ticks trains people to
 * stop reading it.
 *
 * Colour never carries the meaning on its own: every item has an icon and a
 * sentence.
 */

function Item({ icon, tone, headline, detail }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
      <span style={{ display: 'flex', color: tone, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.45, minWidth: 0 }}>
        {headline}
        <br />
        <span style={{ color: T.inkFaint, fontSize: 11.5, ...NUMERIC }}>{detail}</span>
      </span>
    </div>
  );
}

/** The 1px rules must not be the first thing flexbox shrinks away. */

const Divider = () => (
  <div style={{ width: 1, height: 26, background: T.hairline, flexShrink: 0 }} />
);

export default function AttentionStrip({
  stalled,
  stalledMinutes,
  summary,
  canceled,
  canceledValue,
  menuSold,
  menuTotal,
  currency,
  showCancellations,
  isSingleDay,
}) {
  const items = [];

  if (stalled.length) {
    const value = stalled.reduce((sum, order) => sum + Number(order.total || 0), 0);
    items.push({
      key: 'stalled',
      icon: <ClockIcon />,
      tone: T.pendingInk,
      headline: `${stalled.length} ${stalled.length === 1 ? 'ticket' : 'tickets'} open over ${stalledMinutes} min`,
      detail: `${money(value, currency)} sitting on tables`,
    });
  }

  if (summary.discount > 0) {
    items.push({
      key: 'discount',
      icon: <TagIcon />,
      tone: T.inkSoft,
      headline: `${money(summary.discount, currency)} given as discounts`,
      detail: `${summary.discountShare}% of gross, across ${summary.discountedOrders} ${
        summary.discountedOrders === 1 ? 'order' : 'orders'
      }`,
    });
  }

  if (showCancellations && canceled > 0) {
    items.push({
      key: 'canceled',
      icon: <VoidIcon />,
      tone: T.danger,
      headline: `${canceled} ${canceled === 1 ? 'order' : 'orders'} canceled`,
      detail: `${money(canceledValue, currency)} voided`,
    });
  }

  // Only worth saying once there is a day's trading behind it — at nine in the
  // morning "3 of 64 items sold" is not a problem, it is nine in the morning.
  if (menuTotal > 0 && (!isSingleDay || summary.orders >= 10)) {
    items.push({
      key: 'menu',
      icon: <MenuIcon />,
      tone: T.inkSoft,
      headline: `${menuSold} of ${menuTotal} menu items sold`,
      detail: `${menuTotal - menuSold} have not moved`,
    });
  }

  if (!items.length) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        padding: '13px 20px',
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        boxSizing: 'border-box',
      }}
    >
      <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: T.ink }}>
        Worth a look
      </span>
      <Divider />
      {items.map((item, index) => (
        <React.Fragment key={item.key}>
          {index > 0 ? <Divider /> : null}
          <Item icon={item.icon} tone={item.tone} headline={item.headline} detail={item.detail} />
        </React.Fragment>
      ))}
    </div>
  );
}
